import { useEffect, useMemo, useState } from "react";
import type { StoreApi } from "zustand";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  HttpError,
  createPermissionEvaluator,
  retryAfterSeconds,
  resolveLandingRoute,
  resolveFeatureNavigation,
  useAuthStore,
  type AuthController,
  type AuthState,
  type AuthUser,
  type FeatureRegistry,
} from "@nasam/core";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  OTP_MIN_VERIFY_MS,
  OtpCells,
  RESEND_COOLDOWN_SECONDS,
  formatCountdown,
  useCountdown,
  useResendCountdown,
  withOtpVerifyHold,
} from "@nasam/ui";

import { authController as defaultAuthController } from "../../auth";
import { featureRegistry as defaultModuleRegistry } from "../../registry";
import { AuthCardLayout } from "./AuthCardLayout";
import { OtpArtwork } from "./OtpArtwork";
import { retryAfterSecondsFromMessage } from "./retryAfter";

// POST /auth/verify-otp: a code lives 5 minutes and allows 5 attempts; a
// resend issues a fresh one of each.
const OTP_LIFETIME_SECONDS = 5 * 60;
const OTP_MAX_ATTEMPTS = 5;
/** How long the verified state shows before moving on. */
const VERIFIED_HOLD_MS = 1400;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)));
}

export interface OtpChallengeScreenProps {
  readonly authController?: AuthController;
  readonly authStore?: StoreApi<AuthState>;
  readonly registry?: FeatureRegistry;
}

export function OtpChallengeScreen({
  authController = defaultAuthController,
  authStore = useAuthStore,
  registry = defaultModuleRegistry,
}: OtpChallengeScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [pendingUsername, setPendingUsername] = useState(
    () => authStore.getState().pendingOtpUsername,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(OTP_MAX_ATTEMPTS);
  const { secondsLeft, canResend, restart } = useResendCountdown();
  const expiry = useCountdown(OTP_LIFETIME_SECONDS);

  useEffect(() => {
    return authStore.subscribe((state) => {
      setPendingUsername(state.pendingOtpUsername);
    });
  }, [authStore]);

  useEffect(() => {
    if (authStore.getState().pendingOtpUsername === null) {
      void navigate({ to: "/auth/login", replace: true });
    }
  }, [authStore, navigate]);

  const schema = useMemo(
    () =>
      z.object({
        code: z
          .string()
          .min(1, t("validation:codeRequired"))
          .regex(/^\d+$/, t("validation:codeNumeric"))
          .length(6, t("validation:codeLength")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);
    try {
      const startedAt = Date.now();
      await withOtpVerifyHold(
        authController.verifyOtp(values.code, async () => {
          // Finish the verifying fold, then show verified before the stored
          // session moves the router on.
          await sleep(OTP_MIN_VERIFY_MS - (Date.now() - startedAt));
          setVerified(true);
          await sleep(VERIFIED_HOLD_MS);
        }),
      );
      const user = authStore.getState().user;
      if (user !== null) {
        const evaluator = createPermissionEvaluator(user.permissions);
        const visibleNavigation = resolveFeatureNavigation(
          registry.list(),
          evaluator,
        );
        const authUser: AuthUser = {
          ...user,
          permissions: [...user.permissions],
        };
        const target = resolveLandingRoute(authUser, visibleNavigation);
        void navigate({ to: target });
      } else {
        void navigate({ to: "/" });
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes("No OTP login")) {
        void navigate({ to: "/auth/login", replace: true });
        return;
      }
      if (err instanceof HttpError && err.status === 401) {
        // The backend's message tells a spent code apart from a wrong one.
        setAttemptsLeft((left) =>
          err.message.includes("maximum number of attempts")
            ? 0
            : Math.max(0, left - 1),
        );
      }
      setErrorMessage(
        err instanceof Error ? err.message : t("authentication:invalidOtp"),
      );
    }
  };

  const form = useForm({
    defaultValues: { code: "" } satisfies FormValues,
    // A code is typed a digit at a time, so judging it before the first
    // submit would flag every half-entered code; after it, re-check on change
    // so the error clears as soon as the code is fixed.
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  useEffect(() => {
    if (!form.state.isValid) void form.validate("change");
  }, [i18n.language, form]);

  const handleResend = async () => {
    if (!canResend) return;
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      setResending(true);
      await authController.resendOtp();
      setSuccessMessage(t("authentication:otpResentSuccess"));
      restart();
      expiry.start(OTP_LIFETIME_SECONDS);
      setAttemptsLeft(OTP_MAX_ATTEMPTS);
    } catch (err) {
      if (err instanceof Error && err.message.includes("No OTP login")) {
        void navigate({ to: "/auth/login", replace: true });
        return;
      }
      if (err instanceof HttpError && err.status === 429) {
        // Still inside the 60s cooldown or the hourly budget is spent: the
        // link's own countdown takes over from the backend's wait.
        restart(
          retryAfterSeconds(err) ??
            retryAfterSecondsFromMessage(err.message) ??
            RESEND_COOLDOWN_SECONDS,
        );
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : t("common:somethingWentWrong"),
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthCardLayout
      title={t("authentication:otpTitle")}
      subtitle={t("authentication:otpSubtitle", {
        destination: pendingUsername ?? "",
      })}
      artwork={
        <form.Subscribe
          selector={(state) => [state.values.code, state.isSubmitting] as const}
        >
          {([code, isSubmitting]) => (
            <OtpArtwork
              code={code}
              status={
                verified ? "success" : isSubmitting ? "verifying" : "idle"
              }
              error={errorMessage !== null}
              expiresIn={expiry.secondsLeft}
              lifetime={OTP_LIFETIME_SECONDS}
              attemptsLeft={attemptsLeft}
            />
          )}
        </form.Subscribe>
      }
      footer={
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <div className="flex flex-col items-center gap-4">
              <Button
                type="submit"
                form="otp-challenge-form"
                variant="primary"
                className="h-13 w-full"
                disabled={isSubmitting || resending || verified}
              >
                <span
                  key={verified ? "done" : isSubmitting ? "busy" : "idle"}
                  className="animate-in fade-in-0 duration-150"
                >
                  {verified
                    ? t("authentication:verifiedButton")
                    : isSubmitting
                      ? t("authentication:verifyingButton")
                      : t("authentication:verifyButton")}
                </span>
              </Button>

              <p className="text-body-m-r text-text">
                {`${t("authentication:otpDidNotReceive")} `}
                <button
                  type="button"
                  disabled={isSubmitting || resending || verified || !canResend}
                  onClick={() => {
                    void handleResend();
                  }}
                  className="text-primary underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                >
                  {resending
                    ? t("authentication:resendingOtpButton")
                    : canResend
                      ? t("authentication:resendOtpButton")
                      : t("authentication:resendOtpCountdownTime", {
                          time: formatCountdown(secondsLeft),
                        })}
                </button>
              </p>
            </div>
          )}
        </form.Subscribe>
      }
    >
      <Form
        form={form}
        id="otp-challenge-form"
        className="flex w-full flex-col items-center gap-6 px-4 md:px-13 py-4"
        noValidate
      >
        {errorMessage !== null && (
          <div
            role="alert"
            className="animate-in fade-in-0 slide-in-from-top-1 w-full rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
          >
            {errorMessage}
          </div>
        )}

        {successMessage !== null && (
          <div
            role="status"
            className="animate-in fade-in-0 slide-in-from-top-1 w-full rounded-md border border-container-default-border bg-container-default-background p-3 text-body-s-r text-text"
          >
            {successMessage}
          </div>
        )}

        <form.Field name="code">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex w-full flex-col items-center gap-2 space-y-0">
                <FormLabel htmlFor="otp-code" className="sr-only">
                  {t("authentication:otpCodeLabel")}
                </FormLabel>
                <FormControl>
                  <form.Subscribe selector={(state) => state.isSubmitting}>
                    {(isSubmitting) => (
                      <OtpCells
                        id="otp-code"
                        value={field.state.value}
                        onChange={(value) => {
                          field.handleChange(value);
                          setErrorMessage(null);
                        }}
                        onBlur={field.handleBlur}
                        status={
                          verified
                            ? "success"
                            : isSubmitting
                              ? "verifying"
                              : "idle"
                        }
                        error={
                          !field.state.meta.isValid || errorMessage !== null
                        }
                      />
                    )}
                  </form.Subscribe>
                </FormControl>
                <FormMessage className="w-full text-center" />
              </FormItem>
            </FormField>
          )}
        </form.Field>
      </Form>
    </AuthCardLayout>
  );
}
